import React, { useState, useMemo, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart
} from 'recharts';
import { useAppContext } from '../context/AppContext';
import { formatDateForChart } from '../utils';
import { TPoolHistoryType } from '../utils/types';
import { useTheme } from '../context/ThemeContext';
import OverViewItemCard from './OverViewItemCard';
import { MoneyIcon, WalletIcon, LockIcon, ActivityIcon, LoadingIcon } from './SVGIcons';

const PoolBalanceChart: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TPoolHistoryType>('day');
  const [isLoading, setIsLoading] = useState(false);
  const { poolBalanceHistory, poolBalances, getPoolBalance } = useAppContext();
  const { theme } = useTheme();

  useEffect(() => {
    setIsLoading(true);
    getPoolBalance(timeRange);
    // Set loading to false after a short delay to allow data to load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [timeRange])

  const getDataForTimeRange = (range: TPoolHistoryType) => {
    const now = Date.now();
    let startTime: number;
    switch (range) {
      case 'day':
        startTime = now - 24 * 60 * 60 * 1000;
        break;
      case 'week':
        startTime = now - 7 * 24 * 60 * 60 * 1000;
        break;
      case 'month':
        startTime = now - 30 * 24 * 60 * 60 * 1000;
        break;
      case 'year':
        startTime = 0; // Show all available data
        break;
      default:
        startTime = now - 24 * 60 * 60 * 1000;
    }

    // Use a consistent chart data type
    type ChartDataPoint = { date: string; balance: number; staked: number; timestamp: number };
    let chartData: ChartDataPoint[] = poolBalanceHistory
      .filter(entry => entry.timestamp >= startTime)
      .sort((a, b) => a.timestamp - b.timestamp)
      .map(entry => ({
        date: formatDateForChart(entry.timestamp, range),
        balance: entry.pool_balance,
        staked: entry.staked_balance,
        timestamp: entry.timestamp
      }));

    if (range === 'week') {
      // Group by day, keep only the last value for each day
      const groupedByDay: { [date: string]: ChartDataPoint } = {};
      chartData.forEach(entry => {
        const dayKey = new Date(entry.timestamp).toLocaleDateString();
        groupedByDay[dayKey] = { ...entry, date: dayKey };
      });
      chartData = Object.values(groupedByDay)
        .sort((a, b) => a.timestamp - b.timestamp);
    }

    // Group data points based on time range to avoid overcrowding (except for 1W, already grouped)
    const groupedData = (range === 'week') ? chartData : chartData.reduce((acc, curr) => {
      const existingPoint = acc.find(point => point.date === curr.date);
      if (existingPoint) {
        // For overlapping points, use the latest value instead of averaging
        existingPoint.balance = curr.balance;
        existingPoint.staked = curr.staked;
        existingPoint.timestamp = curr.timestamp;
      } else {
        acc.push(curr);
      }
      return acc;
    }, [] as ChartDataPoint[]);

    return groupedData;
  };

  const currentData = useMemo(() => getDataForTimeRange(timeRange), [timeRange, poolBalanceHistory]);
  const lastBalance = poolBalances.total;
  const previousBalance = poolBalanceHistory.length ? poolBalanceHistory[poolBalanceHistory.length - 1].pool_balance : 0;
  const poolBalanceChange = ((lastBalance - previousBalance) / previousBalance) * 100;

  const timeRangeButtons: { label: TPoolHistoryType; text: string }[] = [
    { label: 'day', text: '24H' },
    { label: 'week', text: '1W' },
    { label: 'month', text: '1M' },
    { label: 'year', text: 'All' }
  ];

  // Custom tooltip to show full date and time for 1W
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className='flex flex-col gap-[12px] min-w-[260px] bg-white dark:bg-[#272727] p-[12px] rounded-[10px] shadow-[0_0_10px_0_rgba(0,0,0,0.1)] dark:shadow-[0_0_10px_0_rgba(255,255,255,0.1)]'>
          <div className='text-black/50 dark:text-white/50 text-[14px] leading-[17px]'>{new Date(payload[0].payload.timestamp).toLocaleString()}</div>
          {payload.map((item: any, idx: number) => {
            if (idx > 1) {
              return (
                <div className='flex flex-col gap-[12px]' key={idx}>
                  <div className='w-full h-[1px] bg-[rgba(0,0,0,0.06)] dark:bg-white/10' />
                  <div className='flex gap-[12px] items-center'>
                    <span className='text-black/50 dark:text-white/50 text-[14px] leading-[17px]'>
                      {item.name === 'balance' && 'Free Balance: '}
                      {item.name === 'staked' && 'Staked Balance: '}
                      {item.name === 'total' && 'Total Balance: '}
                    </span>
                    <span style={{ color: item.stroke }} className='text-[24px] leading-[29px]'>
                      τ{item.value.toLocaleString()}
                    </span>
                  </div>
                </div>
              )
            }
            return null;
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-4 sm:space-y-0">
        <h2 className="text-xl text-gray-800 dark:text-white font-borna">Pool Balance</h2>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {timeRangeButtons.map(({ label, text }) => (
            <button
              key={label}
              onClick={() => setTimeRange(label)}
              className={`
                h-[36px]
                px-3 sm:px-[24px]
                py-[12px]
                rounded-[100px]
                font-borna
                leading-none
                transition-colors duration-200
                focus:outline-none
                text-sm
                ${timeRange === label
                  ? 'bg-gray-100 dark:bg-[#232323] text-gray-800 dark:text-white'
                  : 'text-gray-800 dark:text-white'
                }
              `}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
      <div className="h-48 sm:h-64 w-full">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin">
              <LoadingIcon fillColor={theme === 'dark' ? '#fff' : '#000'} className='w-[40px] h-[40px]' />
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={currentData}
              margin={{
                top: 5,
                right: 10,
                left: 0,
                bottom: 5,
              }}
            >
              <XAxis
                dataKey="date"
                stroke="transparent"
                tick={{ fontSize: 10, fill: theme === 'dark' ? '#9CA3AF' : '#6B7280' }}
                angle={-45}
                textAnchor="end"
                height={60}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                stroke="transparent"
                tick={{ fontSize: 10, fill: theme === 'dark' ? '#9CA3AF' : '#6B7280' }}
                tickFormatter={(value) => `τ${value.toLocaleString()}`}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip />}
              />
              {/* Balance shadow */}
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="balance"
                stroke="transparent"
                fill="url(#balanceGradient)"
                animationDuration={0}
              />
              {/* Staked shadow */}
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="staked"
                stroke="transparent"
                fill="url(#stakedGradient)"
                animationDuration={0}
              />
              <defs>
                <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00D4FF" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00D4FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="stakedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6DFF05" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#6DFF05" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="total"
                stroke="#FF7F50"
                strokeWidth={2}
                dot={{ fill: theme === 'dark' ? '#000' : '#fff', stroke: '#FF7F50', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 6, fill: '#FF7F50' }}
                animationDuration={0}
                name="total"
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="balance"
                stroke="#00D4FF"
                strokeWidth={2}
                dot={{ fill: theme === 'dark' ? '#000' : '#fff', stroke: '#00D4FF', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 6, fill: '#00D4FF' }}
                animationDuration={0}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="staked"
                stroke="#6DFF05"
                strokeWidth={2}
                dot={{ fill: theme === 'dark' ? '#000' : '#fff', stroke: '#6DFF05', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 6, fill: '#6DFF05' }}
                animationDuration={0}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <OverViewItemCard cardView={<WalletIcon fillColor={theme === 'dark' ? '#000' : '#fff'} />} value={`τ${lastBalance.toLocaleString()}`} title="Total Balance" />
        <OverViewItemCard cardView={<MoneyIcon fillColor={theme === 'dark' ? '#000' : '#fff'} />} value={`τ${poolBalances.staked.toLocaleString()}`} title="Staked Balance" />
        <OverViewItemCard cardView={<LockIcon fillColor={theme === 'dark' ? '#000' : '#fff'} />} value={`τ${poolBalances.locked.toLocaleString()}`} title="Locked Balance" />
        <OverViewItemCard cardView={<ActivityIcon fillColor={theme === 'dark' ? '#000' : '#fff'} />} value={`${poolBalanceChange >= 0 ? '+' : ''}${poolBalanceChange.toFixed(2)}%`} title="Pool Balance Change" textColor={poolBalanceChange >= 0 ? 'text-lime-400' : 'text-red-500'} iconColor="bg-green-500" />
      </div>
    </div>
  );
};

export default PoolBalanceChart; 